import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./ApproveCandidates.css";
import { X } from "lucide-react";

const ApproveCorporateCandidates = () => {
  const [corporates, setCorporates] = useState([]);
  const [approvedCorporates, setApprovedCorporates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewMode, setViewMode] = useState("table");
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [corporateDetails, setCorporateDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState("");

  const ensureAbsoluteUrl = (url) => {
    if (!url) return "#";
    const trimmedUrl = url.trim();
    if (/^https?:\/\//i.test(trimmedUrl)) {
      return trimmedUrl;
    }
    return `https://${trimmedUrl}`;
  };

  useEffect(() => {
    fetchCorporates();
    fetchApprovedCorporates();
  }, []);

  const fetchCorporates = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL || "http://localhost:3500"
        }/api/admin/corporate`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch corporate profiles");
      }

      const data = await response.json();

      // Deduplicate data based on id
      const uniqueData = Array.from(
        new Map(data.map((item) => [item.id, item])).values(),
      );

      setCorporates(uniqueData);
    } catch (error) {
      setError("Failed to fetch corporate profiles");
      console.error("Error fetching corporates:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovedCorporates = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL || "http://localhost:3500"
        }/api/admin/corporate/approved`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch approved corporate profiles");
      }

      const data = await response.json();
      setApprovedCorporates(data);
    } catch (error) {
      console.error("Error fetching approved corporate profiles:", error);
    }
  };

  const handleApprove = async (corporateId) => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL || "http://localhost:3500"
        }/api/admin/corporate/${corporateId}/approve`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to approve corporate profile");
      }

      // Update the corporate status in the local state
      setCorporates(
        corporates.map((corporate) =>
          corporate.id === corporateId
            ? { ...corporate, review_status: "Approved" }
            : corporate,
        ),
      );

      // Refresh the approved corporates list
      fetchApprovedCorporates();
    } catch (error) {
      setError("Failed to approve corporate profile");
      console.error("Error approving corporate:", error);
    }
  };

  const handleReject = async (corporateId) => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL || "http://localhost:3500"
        }/api/admin/corporate/${corporateId}/reject`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to reject corporate profile");
      }

      // Update the corporate status in the local state for both lists
      setCorporates(
        corporates.map((corporate) =>
          corporate.id === corporateId
            ? { ...corporate, review_status: "Rejected" }
            : corporate,
        ),
      );
      setApprovedCorporates(
        approvedCorporates.map((corporate) =>
          corporate.id === corporateId
            ? { ...corporate, review_status: "Rejected" }
            : corporate,
        ),
      );
    } catch (error) {
      setError("Failed to reject corporate profile");
      console.error("Error rejecting corporate:", error);
    }
  };

  const handleToggleActive = async (corporateId, currentIsActive) => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL || "http://localhost:3500"
        }/api/admin/corporate/${corporateId}/toggle-active`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ isActive: !currentIsActive }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to toggle active status");
      }

      // Update the corporate status in the local state
      setApprovedCorporates(
        approvedCorporates.map((corporate) =>
          corporate.id === corporateId
            ? { ...corporate, isActive: !currentIsActive }
            : corporate,
        ),
      );
    } catch (error) {
      setError("Failed to toggle active status");
      console.error("Error toggling active status:", error);
    }
  };

  const handleViewDetails = async (corporateId) => {
    setLoadingDetails(true);
    setDetailsError("");
    setShowDetailsDialog(true);
    setCorporateDetails(null);

    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL || "http://localhost:3500"
        }/api/admin/corporate/details/${corporateId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch corporate details");
      }

      const data = await response.json();
      setCorporateDetails(data);
    } catch (error) {
      setDetailsError("Failed to load corporate details");
      console.error("Error fetching corporate details:", error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseDetailsDialog = () => {
    setShowDetailsDialog(false);
    setCorporateDetails(null);
    setDetailsError("");
  };

  if (loading) {
    return (
      <div className="main-content">
        <div className="container">
          <div className="loading-spinner">Loading corporate profiles...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="container">
        <div className="page-header">
          <Link to="/" className="back-link">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
            Back to Dashboard
          </Link>
          <h1 className="page-title">Approve Corporate</h1>
          <div className="view-toggle" role="tablist" aria-label="View mode">
            <button
              type="button"
              className={`toggle-btn ${viewMode === "table" ? "active" : ""}`}
              onClick={() => setViewMode("table")}
              role="tab"
              aria-selected={viewMode === "table"}
            >
              Table
            </button>
            <button
              type="button"
              className={`toggle-btn ${viewMode === "grid" ? "active" : ""}`}
              onClick={() => setViewMode("grid")}
              role="tab"
              aria-selected={viewMode === "grid"}
            >
              Grid
            </button>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* Pending Approvals Section */}
        <div className="section-header">
          <h2>Pending Approvals</h2>
        </div>

        {corporates.length === 0 ? (
          <div className="empty-state">
            <h3>No corporate profiles found</h3>
            <p>
              There are currently no corporate profiles waiting for approval.
            </p>
          </div>
        ) : (
          <>
            {viewMode === "table" ? (
              <div className="table-wrap">
                <table className="candidates-table">
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Website</th>
                      <th>Decision</th>
                    </tr>
                  </thead>
                  <tbody>
                    {corporates.map((c) => (
                      <tr key={c.id}>
                        <td>
                          <div className="cell-user">
                            <div className="avatar-sm">
                              <span>{(c.company_name || "C")[0]}</span>
                            </div>
                            <div className="cell-user-meta">
                              <div className="name">
                                {c.company_name || "Unnamed Company"}
                              </div>
                              <div className="email">{c.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {(() => {
                            const raw = (
                              c.review_status || "Under Review"
                            ).toString();
                            const cls = raw
                              .toLowerCase()
                              .replace(/\s+/g, "-")
                              .replace(/[^a-z-]/g, "");
                            return (
                              <span
                                className={`status-pill ${cls || "pending"}`}
                              >
                                {raw}
                              </span>
                            );
                          })()}
                        </td>
                        <td>
                          {new Date(c.created_at).toLocaleDateString()}{" "}
                          <span className="muted">
                            {new Date(c.created_at).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </td>
                        <td>
                          {c.website ? (
                            <a
                              className="link-action"
                              href={ensureAbsoluteUrl(c.website)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Visit
                            </a>
                          ) : (
                            <span className="muted">Not set</span>
                          )}
                        </td>
                        <td className="decision-cell-corporate">
                          {c.review_status === "Under Review" && (
                            <>
                              {" "}
                              <button
                                className="btn btn-success btn-sm"
                                onClick={() => handleApprove(c.id)}
                              >
                                Approve
                              </button>{" "}
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => handleReject(c.id)}
                              >
                                Reject
                              </button>
                            </>
                          )}
                          <a
                            className="link-action"
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              handleViewDetails(c.user_id);
                            }}
                          >
                            View Details
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="candidates-grid">
                {corporates.map((corporate) => (
                  <div key={corporate.id} className="candidate-card">
                    <div className="candidate-header">
                      <div className="candidate-avatar corporate">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M3 21h18"></path>
                          <path d="M3 7h18"></path>
                          <path d="M3 3h18"></path>
                          <path d="M7 21V7"></path>
                          <path d="M17 21V7"></path>
                        </svg>
                      </div>
                      <div className="candidate-info">
                        <h3>{corporate.company_name || "Unnamed Company"}</h3>
                        <p>{corporate.email}</p>
                      </div>
                      <div
                        className={`status-badge ${
                          corporate.review_status?.toLowerCase() || "pending"
                        }`}
                      >
                        {corporate.review_status || "Under Review"}
                      </div>
                    </div>

                    <div className="candidate-details">
                      <div className="detail-item">
                        <strong>Created:</strong>{" "}
                        {new Date(corporate.created_at).toLocaleDateString()}
                      </div>
                      {corporate.industry && (
                        <div className="detail-item">
                          <strong>Industry:</strong> {corporate.industry}
                        </div>
                      )}
                      {corporate.company_size && (
                        <div className="detail-item">
                          <strong>Company Size:</strong>{" "}
                          {corporate.company_size}
                        </div>
                      )}
                      {corporate.website && (
                        <div className="detail-item">
                          <strong>Website:</strong>
                          <a
                            href={ensureAbsoluteUrl(corporate.website)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {corporate.website}
                          </a>
                        </div>
                      )}
                    </div>

                    <div className="detail-item">
                      <a
                        className="link-action"
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleViewDetails(corporate.user_id);
                        }}
                      >
                        View Details
                      </a>{" "}
                    </div>

                    <div className="candidate-actions">
                      {corporate.review_status === "Under Review" && (
                        <>
                          {" "}
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => handleApprove(corporate.id)}
                          >
                            Approve
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleReject(corporate.id)}
                          >
                            Reject
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Approved Corporates Section */}
        <div className="section-header">
          <h2>Approved Corporates</h2>
        </div>

        {approvedCorporates.length === 0 ? (
          <div className="empty-state">
            <h3>No approved corporate profiles</h3>
            <p>There are currently no approved corporate profiles.</p>
          </div>
        ) : (
          <>
            {viewMode === "table" ? (
              <div className="table-wrap">
                <table className="candidates-table">
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th>Active</th>
                      <th>Created</th>
                      <th>Website</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {approvedCorporates.map((c) => (
                      <tr key={c.id}>
                        <td>
                          <div className="cell-user">
                            <div className="avatar-sm">
                              <span>{(c.company_name || "C")[0]}</span>
                            </div>
                            <div className="cell-user-meta">
                              <div className="name">
                                {c.company_name || "Unnamed Company"}
                              </div>
                              <div className="email">{c.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`status-pill ${
                              c.isActive ? "approved" : "rejected"
                            }`}
                          >
                            {c.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          {new Date(c.created_at).toLocaleDateString()}{" "}
                          <span className="muted">
                            {new Date(c.created_at).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </td>
                        <td>
                          {c.website ? (
                            <a
                              className="link-action"
                              href={ensureAbsoluteUrl(c.website)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Visit
                            </a>
                          ) : (
                            <span className="muted">Not set</span>
                          )}
                        </td>
                        <td className="decision-cell-corporate">
                          <button
                            className={`btn btn-sm ${
                              c.isActive ? "btn-warning" : "btn-success"
                            }`}
                            onClick={() => handleToggleActive(c.id, c.isActive)}
                          >
                            {c.isActive ? "Deactivate" : "Activate"}
                          </button>{" "}
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleReject(c.id)}
                          >
                            Reject
                          </button>
                          <a
                            className="link-action"
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              handleViewDetails(c.user_id);
                            }}
                          >
                            View Details
                          </a>{" "}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="candidates-grid">
                {approvedCorporates.map((corporate) => (
                  <div key={corporate.id} className="candidate-card">
                    <div className="candidate-header">
                      <div className="candidate-avatar corporate">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M3 21h18"></path>
                          <path d="M3 7h18"></path>
                          <path d="M3 3h18"></path>
                          <path d="M7 21V7"></path>
                          <path d="M17 21V7"></path>
                        </svg>
                      </div>
                      <div className="candidate-info">
                        <h3>{corporate.company_name || "Unnamed Company"}</h3>
                        <p>{corporate.email}</p>
                      </div>
                      <div
                        className={`status-badge ${
                          corporate.isActive ? "active" : "inactive"
                        }`}
                      >
                        {corporate.isActive ? "Active" : "Inactive"}
                      </div>
                    </div>

                    <div className="candidate-details">
                      <div className="detail-item">
                        <strong>Created:</strong>{" "}
                        {new Date(corporate.created_at).toLocaleDateString()}
                      </div>
                      {corporate.industry && (
                        <div className="detail-item">
                          <strong>Industry:</strong> {corporate.industry}
                        </div>
                      )}
                      {corporate.company_size && (
                        <div className="detail-item">
                          <strong>Company Size:</strong>{" "}
                          {corporate.company_size}
                        </div>
                      )}
                      {corporate.website && (
                        <div className="detail-item">
                          <strong>Website:</strong>
                          <a
                            href={ensureAbsoluteUrl(corporate.website)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {corporate.website}
                          </a>
                        </div>
                      )}
                    </div>

                    <div className="detail-item">
                      <a
                        className="link-action"
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleViewDetails(corporate.user_id);
                        }}
                      >
                        View Details
                      </a>{" "}
                    </div>

                    <div className="candidate-actions">
                      <button
                        className={`btn btn-sm ${
                          corporate.isActive ? "btn-warning" : "btn-success"
                        }`}
                        onClick={() =>
                          handleToggleActive(corporate.id, corporate.isActive)
                        }
                      >
                        {corporate.isActive ? "Deactivate" : "Activate"}
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleReject(corporate.id)}
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Details Dialog */}
        {showDetailsDialog && (
          <div className="modal-overlay" onClick={handleCloseDetailsDialog}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <h2>Corporate Details</h2>
                  <p className="modal-subtitle">
                    Complete information about the corporate profile
                  </p>
                </div>
                <button
                  className="modal-close"
                  onClick={handleCloseDetailsDialog}
                  aria-label="Close dialog"
                >
                  <X size={24} />
                </button>
              </div>
              <div className="modal-body">
                {loadingDetails ? (
                  <div className="loading">Loading details...</div>
                ) : detailsError ? (
                  <div className="error">{detailsError}</div>
                ) : corporateDetails ? (
                  <div className="candidate-details-content">
                    {/* Basic Information */}
                    <div className="detail-section">
                      <h3>Basic Information</h3>
                      <div className="detail-row">
                        <strong>Company Name:</strong>
                        <span>{corporateDetails.name || "Not specified"}</span>
                      </div>
                      <div className="detail-row">
                        <strong>Industry:</strong>
                        <span>
                          {corporateDetails.industry || "Not specified"}
                        </span>
                      </div>
                      <div className="detail-row">
                        <strong>Company Type:</strong>
                        <span>
                          {corporateDetails.company_type || "Not specified"}
                        </span>
                      </div>
                      <div className="detail-row">
                        <strong>Company Size:</strong>
                        <span>
                          {corporateDetails.size
                            ? `${corporateDetails.size} employees`
                            : "Not specified"}
                        </span>
                      </div>
                      {/* <div className="detail-row">
                        <strong>Company Value:</strong>
                        <span>
                          {corporateDetails.company_value || "Not specified"}
                        </span>
                      </div> */}
                      <div className="detail-row">
                        <strong>Website:</strong>
                        <span>
                          {corporateDetails.url ? (
                            <a
                              href={ensureAbsoluteUrl(corporateDetails.url)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {corporateDetails.url}
                            </a>
                          ) : (
                            "Not specified"
                          )}
                        </span>
                      </div>
                      <div className="detail-row">
                        <strong>Description:</strong>
                        <span>
                          {corporateDetails.description || "Not specified"}
                        </span>
                      </div>
                    </div>

                    {/* Contact Information */}
                    <div className="detail-section">
                      <h3>Contact Information</h3>
                      <div className="detail-row">
                        <strong>Business Email:</strong>
                        <span>
                          {corporateDetails.business_email || "Not specified"}
                        </span>
                      </div>
                      <div className="detail-row">
                        <strong>Email Domain:</strong>
                        <span>
                          {corporateDetails.email_domain || "Not specified"}
                        </span>
                      </div>
                      <div className="detail-row">
                        <strong>Phone Number:</strong>
                        <span>
                          {corporateDetails.phone_num || "Not specified"}
                        </span>
                      </div>
                    </div>

                    {/* Contact Person */}
                    <div className="detail-section">
                      <h3>Contact Person</h3>
                      <div className="detail-row">
                        <strong>Name:</strong>
                        <span>
                          {corporateDetails.contact_person_name ||
                            "Not specified"}
                        </span>
                      </div>
                      <div className="detail-row">
                        <strong>Email:</strong>
                        <span>
                          {corporateDetails.contact_person_email ||
                            "Not specified"}
                        </span>
                      </div>
                      <div className="detail-row">
                        <strong>Phone:</strong>
                        <span>
                          {corporateDetails.contact_person_phone ||
                            "Not specified"}
                        </span>
                      </div>
                    </div>

                    {/* Work Environment & Culture */}
                    {(corporateDetails.work_environment ||
                      corporateDetails.culture_values) && (
                      <div className="detail-section">
                        <h3>Work Environment & Culture</h3>
                        {corporateDetails.work_environment &&
                          Array.isArray(corporateDetails.work_environment) &&
                          corporateDetails.work_environment.length > 0 && (
                            <div className="detail-row">
                              <strong>Work Environment:</strong>
                              <span>
                                {corporateDetails.work_environment.join(", ")}
                              </span>
                            </div>
                          )}
                        {corporateDetails.culture_values && (
                          <div className="detail-row">
                            <strong>Culture Values:</strong>
                            <span>{corporateDetails.culture_values}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Additional Information */}
                    <div className="detail-section">
                      <h3>Additional Information</h3>
                      <div className="detail-row">
                        <strong>Created At:</strong>
                        <span>
                          {new Date(
                            corporateDetails.created_at,
                          ).toLocaleString()}
                        </span>
                      </div>
                      <div className="detail-row">
                        <strong>Last Updated:</strong>
                        <span>
                          {new Date(
                            corporateDetails.updated_at,
                          ).toLocaleString()}
                        </span>
                      </div>
                      {corporateDetails.logo && (
                        <div className="detail-row">
                          <strong>Logo:</strong>
                          <span>
                            <img
                              src={corporateDetails.logo}
                              alt="Company logo"
                              style={{
                                maxWidth: "200px",
                                maxHeight: "200px",
                                borderRadius: "8px",
                              }}
                            />
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApproveCorporateCandidates;
